/**
 * Day boundaries. A diary day is the user's local day, not UTC — logging a
 * midnight snack in Vilnius must not land on tomorrow's page because the server
 * runs in UTC. The client computes `logDate` and sends the zone it used.
 */

/** `YYYY-MM-DD` in the given IANA zone (defaults to the device's zone). */
export function localDateString(instant: Date = new Date(), timeZone?: string): string {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  })
  return fmt.format(instant)
}

export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'
}

/**
 * The wall-clock hour an instant happened at, 0–23, in the zone it happened in.
 *
 * Stored entries carry both the instant and the zone the phone was in, which is
 * what makes "that coffee was at four in the afternoon" survive a flight. Null
 * when either is unusable rather than quietly falling back to UTC, because an
 * hour that is wrong by three is worse than no hour at all.
 */
export function localHourOf(instant: string | null, timeZone?: string | null): number | null {
  if (instant == null) return null
  const parsed = Date.parse(instant)
  if (!Number.isFinite(parsed)) return null
  try {
    const hour = new Intl.DateTimeFormat('en-GB', {
      timeZone: timeZone ?? undefined,
      hour: '2-digit',
      hourCycle: 'h23',
    }).format(parsed)
    const value = Number(hour)
    return Number.isInteger(value) && value >= 0 && value <= 23 ? value : null
  } catch {
    // An unknown IANA zone from an old row should not take the screen down.
    return null
  }
}

/** Shift a `YYYY-MM-DD` string by whole days without touching time zones. */
export function addDays(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

export function daysBetween(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`)
  const b = Date.parse(`${to}T00:00:00Z`)
  return Math.round((b - a) / 86_400_000)
}

/** Sunday-safe week start (Monday) for the trends screen. */
export function startOfWeek(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number]
  const dt = new Date(Date.UTC(y, m - 1, d))
  const shift = (dt.getUTCDay() + 6) % 7
  return addDays(dateStr, -shift)
}

const LT_WEEKDAYS = [
  'Pirmadienis',
  'Antradienis',
  'Trečiadienis',
  'Ketvirtadienis',
  'Penktadienis',
  'Šeštadienis',
  'Sekmadienis',
] as const

export function weekdayNameLt(dateStr: string): string {
  const [y, m, d] = dateStr.split('-').map(Number) as [number, number, number]
  const idx = (new Date(Date.UTC(y, m - 1, d)).getUTCDay() + 6) % 7
  return LT_WEEKDAYS[idx]!
}

/**
 * "pirmadieniais" — the weekday as a habit rather than a date.
 *
 * Lithuanian says "on Mondays" with the instrumental plural, which is the form
 * any sentence about a recurring pattern needs: „Pirmadieniais viršyta trys iš
 * keturių" reads as an observation, where the nominative would read as a
 * calendar entry. Lowercase, because it lands mid-sentence more often than not.
 */
export function weekdayHabitLt(dateStr: string): string {
  return `${weekdayNameLt(dateStr).slice(0, -2).toLowerCase()}iais`
}

/** "Šiandien" / "Vakar" / a date, for the diary header. */
export function relativeDayLabel(dateStr: string, today = localDateString()): string {
  const diff = daysBetween(dateStr, today)
  if (diff === 0) return 'Šiandien'
  if (diff === 1) return 'Vakar'
  if (diff === -1) return 'Rytoj'
  return dateStr
}
